import { useCallback, useEffect, useRef, type RefObject } from 'react';
import type { GridApi } from '@mui/x-data-grid-premium';
import {
  applyGridColumns,
  applyGridLayout,
  exportGridLayout,
  readSavedLayout,
  writeSavedLayout,
} from './gridState';

const SAVE_DEBOUNCE_MS = 300;

/**
 * Persist DataGrid Premium layout (column visibility, order, width, sorting,
 * filtering, grouping, aggregation, pinning, pagination, density) to localStorage
 * — the transient "where I left off on this device" layer. Named, cross-device
 * saved views are handled by GridViewSelector; both share the helpers in
 * ./gridState so the payloads are identical.
 *
 * Usage:
 *   const apiRef = useGridApiRef();
 *   useGridStatePersistence(apiRef, 'Sales', 'Order');
 */
export function useGridStatePersistence(
  apiRef: RefObject<GridApi | null>,
  module: string,
  entity: string,
  /** Optional suffix for child grids, e.g. "OrderLine-inline" */
  variant?: string,
  options?: {
    /**
     * Persist the user's column order (default true). Set false for grids whose
     * column order is owned by configuration (e.g. Plan Entry, where measure
     * sortOrder dictates it) — the stored order is then neither written nor
     * applied, so a payload saved before the configuration changed can never
     * push new columns out of place.
     */
    columnOrder?: boolean;
    /** Persist pinned columns (default true). Set false when pinning is programmatic. */
    pinning?: boolean;
  },
) {
  const gridKey = `${module}:${entity}${variant ? ':' + variant : ''}`;
  const persistColumnOrder = options?.columnOrder !== false;
  const persistPinning = options?.pinning !== false;

  // Strips the excluded facets from a layout. Used on BOTH save and apply: apply-side
  // stripping is what heals layouts already stored with the old shape.
  const stripExcluded = useCallback((layout: any) => {
    if (!layout) return layout;
    if (!persistColumnOrder && layout.columns?.orderedFields) delete layout.columns.orderedFields;
    if (!persistPinning) delete layout.pinnedColumns;
    return layout;
  }, [persistColumnOrder, persistPinning]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards against the restore -> columnsChange -> restore feedback loop.
  // restoreState publishes events synchronously, so a simple flag is enough.
  const isRestoringRef = useRef(false);
  // Set for the duration of the current event-loop tick whenever the user drives
  // a column/model change. The columnsChange re-apply (below) reads it from a
  // microtask — which runs after ALL synchronous events in the tick — so it is
  // robust to the order in which MUI fires the events.
  const userActionRef = useRef(false);

  const markUserAction = useCallback(() => {
    userActionRef.current = true;
    // Clear on a macrotask: it runs AFTER the columnsChange microtask of this
    // tick, but before any later tick's events.
    setTimeout(() => {
      userActionRef.current = false;
    }, 0);
  }, []);

  // Write the current grid layout to localStorage. Skips writes triggered by our
  // own restoreState call.
  const writeNow = useCallback(() => {
    if (isRestoringRef.current) return;
    const layout = stripExcluded(exportGridLayout(apiRef));
    if (layout) writeSavedLayout(gridKey, layout);
  }, [apiRef, gridKey, stripExcluded]);

  // Debounced save
  const save = useCallback(() => {
    if (isRestoringRef.current) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(writeNow, SAVE_DEBOUNCE_MS);
  }, [writeNow]);

  // Read the stored layout and apply it to the grid (full — used on mount).
  const restore = useCallback(() => {
    // readSavedLayout returns a fresh parse, so stripExcluded may mutate it freely.
    const saved = stripExcluded(readSavedLayout(gridKey));
    if (!saved) return;
    isRestoringRef.current = true;
    try {
      applyGridLayout(apiRef, saved);
    } finally {
      isRestoringRef.current = false;
    }
  }, [apiRef, gridKey, stripExcluded]);

  // Re-apply only the column layout — used when a column rebuild resets
  // width/order. Never touches filter/sort/grouping, so it can't clobber them.
  const restoreColumns = useCallback(() => {
    const saved = stripExcluded(readSavedLayout(gridKey));
    if (!saved?.columns) return;
    isRestoringRef.current = true;
    try {
      applyGridColumns(apiRef, saved);
    } finally {
      isRestoringRef.current = false;
    }
  }, [apiRef, gridKey, stripExcluded]);

  // Restore on mount
  useEffect(() => {
    restore();
  }, [restore]);

  // Subscribe to state change events
  useEffect(() => {
    const api = apiRef.current;
    if (!api?.subscribeEvent) return;

    // User-driven changes worth persisting. Each also marks the tick as a user
    // action so the columnsChange re-apply doesn't revert it.
    const saveEvents = [
      'columnVisibilityModelChange',
      'columnOrderChange',
      'sortModelChange',
      'filterModelChange',
      'columnWidthChange',
      'columnResizeStop',
      'paginationModelChange',
      'densityChange',
      'rowGroupingModelChange',
      'aggregationModelChange',
      'pinnedColumnsChange',
      'pivotModeChange',
      'pivotModelChange',
    ] as const;

    // Fire mid-gesture (e.g. during a column drag-reorder) but aren't worth a
    // save on their own — they only need to mark the tick as user-driven.
    const markOnlyEvents = ['columnIndexChange'] as const;

    const unsubscribers: Array<() => void> = [];

    for (const event of saveEvents) {
      unsubscribers.push(
        api.subscribeEvent(event as any, () => {
          markUserAction();
          save();
        }),
      );
    }
    for (const event of markOnlyEvents) {
      unsubscribers.push(api.subscribeEvent(event as any, markUserAction));
    }

    // The column set can be rebuilt after mount — async enum valueOptions /
    // field restrictions change the `columns` prop, and MUI rebuilds column
    // state, resetting width/order to the colDef defaults (wiping what we
    // restored). Re-apply the saved layout when that happens.
    //
    // But MUI also publishes columnsChange for user-driven changes (resize,
    // reorder, grouping, pinning, aggregation…). We defer to a microtask and
    // skip if the tick was user-driven — the userActionRef flag is set
    // synchronously by the events above, so by the time the microtask runs it
    // reflects everything that happened this tick, regardless of event order.
    unsubscribers.push(
      api.subscribeEvent('columnsChange' as any, () => {
        if (isRestoringRef.current) return;
        queueMicrotask(() => {
          if (userActionRef.current) return; // user-driven column change — keep it
          restoreColumns();
        });
      }),
    );

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        // Flush any pending debounced save so a quick navigation away
        // (within the debounce window) doesn't drop the user's last edit.
        writeNow();
      }
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [apiRef, save, restoreColumns, writeNow, markUserAction]);
}
